import BaseAPIService from './baseAPI';

// Analytics Types matching backend implementation
export interface AnalyticsSummary {
  period: {
    type: string;
    start: string;
    end: string;
  };
  metrics: {
    total_posts: number;
    published_posts: number;
    total_views: number;
    total_likes: number;
    total_shares: number;
    total_comments: number;
    avg_engagement_rate: number;
  };
  usage: {
    ai_generations: number;
    image_searches: number;
    social_posts: number;
  };
  best_performing_post?: {
    id: string;
    topic: string;
    engagement_rate: number;
  };
}

export interface PostAnalytics {
  post_id: string;
  topic: string;
  content_preview: string;
  published_at: string;
  metrics: {
    views: number;
    likes: number;
    shares: number;
    comments: number;
    engagement_rate: number;
    reach: number;
    impressions: number;
    click_through_rate?: number;
  };
  platform_breakdown: {
    linkedin?: {
      views: number;
      engagement_rate: number;
      best_time: string;
    };
    twitter?: {
      views: number;
      engagement_rate: number;
      best_time: string;
    };
    instagram?: {
      views: number;
      engagement_rate: number;
      best_time: string;
    };
  };
  engagement_timeline: Array<{
    hour: number;
    views: number;
    likes: number;
    shares: number;
  }>;
  audience_insights: {
    demographics: {
      age_groups: Record<string, number>;
      gender: Record<string, number>;
    };
    geography: Record<string, number>;
    interests: string[];
  };
}

export interface AdvancedAnalytics {
  demographics: {
    age_distribution: Record<string, number>;
    gender_distribution: Record<string, number>;
    education_level: Record<string, number>;
  };
  geographic_data: {
    countries: Record<string, number>;
    cities: Record<string, number>;
    timezones: Record<string, number>;
  };
  device_analytics: {
    device_types: Record<string, number>;
    browsers: Record<string, number>;
    operating_systems: Record<string, number>;
  };
  time_analytics: {
    best_posting_times: Array<{
      hour: number;
      day: string;
      engagement_rate: number;
    }>;
    audience_activity: Array<{
      hour: number;
      activity_level: number;
    }>;
  };
}

export interface TrendAnalytics {
  metric: string;
  period: string;
  timeframe: number;
  trends: Array<{
    period: string;
    date: string;
    value: number;
    change_percent: number;
  }>;
  summary: {
    average: number;
    highest: {
      period: string;
      value: number;
    };
    lowest: {
      period: string;
      value: number;
    };
    trend_direction: 'upward' | 'downward' | 'stable';
  };
}

export interface PostComparison {
  posts: Array<{
    post_id: string;
    topic: string;
    content_preview: string;
    created_at: string;
    metrics: Record<string, number>;
  }>;
  comparison_insights: {
    [metric: string]: {
      best_performing: {
        post_id: string;
        topic: string;
      };
      worst_performing: {
        post_id: string;
        topic: string;
      };
      average: number;
      range: number;
    };
  };
  metrics_analyzed: string[];
}

export interface ExportData {
  format: 'csv' | 'json' | 'excel';
  content: string;
  filename: string;
}

// Legacy interfaces (keeping for backward compatibility)
export interface PlatformAnalytics {
  platform: string;
  posts_count: number;
  total_engagement: number;
  average_engagement: number;
  best_performing_post: string;
  follower_growth: number;
  reach: number;
}

export interface TimeSeriesData {
  date: string;
  views: number;
  engagement: number;
  posts_published: number;
}

export interface EngagementAnalytics {
  hourly_engagement: Array<{ hour: number; engagement: number }>;
  daily_engagement: Array<{ day: string; engagement: number }>;
  monthly_engagement: Array<{ month: string; engagement: number }>;
}

export interface ContentAnalytics {
  top_keywords: Array<{ keyword: string; frequency: number; engagement: number }>;
  top_hashtags: Array<{ hashtag: string; usage: number; engagement: number }>;
  content_types: Array<{ type: string; count: number; avg_engagement: number }>;
  optimal_length: { min: number; max: number; avg_engagement: number };
}

class AnalyticsAPIService extends BaseAPIService {
  constructor() {
    super();
  }

  // NEW ANALYTICS ENDPOINTS - Matching backend implementation

  // User Activity Summary
  async getSummary(period = 'monthly'): Promise<{ data: AnalyticsSummary }> {
    return this.get<{ data: AnalyticsSummary }>(`/analytics/summary?period=${period}`);
  }

  // Detailed Post Analytics
  async getPostAnalytics(postId: string): Promise<{ data: PostAnalytics }> {
    return this.get<{ data: PostAnalytics }>(`/analytics/posts/${postId}`);
  }

  // All Posts Analytics with Pagination
  async getAllPostsAnalytics(params: {
    limit?: number;
    offset?: number;
    sort_by?: string;
    order?: 'asc' | 'desc';
  } = {}): Promise<{
    data: {
      posts: Array<{
        id: string;
        topic: string;
        content_preview: string;
        status: string;
        created_at: string;
        engagement_rate: number;
        views: number;
        likes: number;
        shares: number;
      }>;
      pagination: {
        total: number;
        limit: number;
        offset: number;
        pages: number;
      };
      sorting: {
        sort_by: string;
        order: string;
      };
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit.toString());
    if (params.offset) queryParams.append('offset', params.offset.toString());
    if (params.sort_by) queryParams.append('sort_by', params.sort_by);
    if (params.order) queryParams.append('order', params.order);

    return this.get<any>(`/analytics/posts?${queryParams.toString()}`);
  }

  // Advanced Analytics (Pro+ plans)
  async getAdvancedAnalytics(metrics?: string[]): Promise<{ data: AdvancedAnalytics }> {
    const queryParams = new URLSearchParams();
    if (metrics && metrics.length > 0) {
      queryParams.append('metrics', metrics.join(','));
    }

    return this.get<{ data: AdvancedAnalytics }>(`/analytics/advanced?${queryParams.toString()}`);
  }

  // Analytics Trends
  async getAnalyticsTrends(params: {
    metric: string;
    period: string;
    timeframe: number;
  }): Promise<{ data: TrendAnalytics }> {
    const queryParams = new URLSearchParams();
    queryParams.append('metric', params.metric);
    queryParams.append('period', params.period);
    queryParams.append('timeframe', params.timeframe.toString());

    return this.get<{ data: TrendAnalytics }>(`/analytics/trends?${queryParams.toString()}`);
  }

  // Posts Comparison
  async comparePostsAnalytics(params: {
    post_ids: string[];
    metrics?: string[];
  }): Promise<{ data: PostComparison }> {
    const queryParams = new URLSearchParams();
    queryParams.append('post_ids', params.post_ids.join(','));
    if (params.metrics) {
      queryParams.append('metrics', params.metrics.join(','));
    }

    return this.get<{ data: PostComparison }>(`/analytics/comparison?${queryParams.toString()}`);
  }

  // Export Analytics Data
  async exportAnalytics(params: {
    format: 'csv' | 'json' | 'excel';
    date_range?: {
      start: string;
      end: string;
    };
    metrics?: string[];
    include_demographics?: boolean;
  }): Promise<{ data: ExportData }> {
    return this.post<{ data: ExportData }>('/analytics/export', params);
  }

  // LEGACY METHODS (keeping for backward compatibility)

  async getDashboardData(): Promise<{ 
    data: {
      summary: AnalyticsSummary;
      recent_posts: PostAnalytics[];
      engagement_trend: TimeSeriesData[];
    }
  }> {
    return this.get<{ 
      data: {
        summary: AnalyticsSummary;
        recent_posts: PostAnalytics[];
        engagement_trend: TimeSeriesData[];
      }
    }>('/analytics/dashboard');
  }

  async getTopPerformingPosts(limit = 10, period = '30d'): Promise<{ data: PostAnalytics[] }> {
    return this.get<{ data: PostAnalytics[] }>(`/analytics/posts/top?limit=${limit}&period=${period}`);
  }

  // Platform Analytics
  async getPlatformAnalytics(platform?: string): Promise<{ data: PlatformAnalytics[] | PlatformAnalytics }> {
    const url = platform ? `/analytics/platforms/${platform}` : '/analytics/platforms';
    return this.get<{ data: PlatformAnalytics[] | PlatformAnalytics }>(url);
  }

  async compareplatforms(platforms: string[], period = '30d'): Promise<{ data: PlatformAnalytics[] }> {
    return this.post<{ data: PlatformAnalytics[] }>('/analytics/platforms/compare', {
      platforms,
      period
    });
  }

  // Time Series Analytics
  async getEngagementTrend(period = '30d', granularity = 'daily'): Promise<{ data: TimeSeriesData[] }> {
    return this.get<{ data: TimeSeriesData[] }>(`/analytics/engagement-trend?period=${period}&granularity=${granularity}`);
  }

  async getEngagementByTime(): Promise<{ data: EngagementAnalytics }> {
    return this.get<{ data: EngagementAnalytics }>('/analytics/engagement-by-time');
  }

  async getPublishingTrend(period = '30d'): Promise<{ data: Array<{ date: string; posts_count: number }> }> {
    return this.get<{ data: Array<{ date: string; posts_count: number }> }>(`/analytics/publishing-trend?period=${period}`);
  }

  // Content Analytics
  async getContentAnalytics(period = '30d'): Promise<{ data: ContentAnalytics }> {
    return this.get<{ data: ContentAnalytics }>(`/analytics/content?period=${period}`);
  }

  async getHashtagPerformance(period = '30d'): Promise<{ data: Array<{ hashtag: string; usage: number; avg_engagement: number }> }> {
    return this.get<{ data: Array<{ hashtag: string; usage: number; avg_engagement: number }> }>(`/analytics/hashtags?period=${period}`);
  }

  async getKeywordPerformance(period = '30d'): Promise<{ data: Array<{ keyword: string; frequency: number; avg_engagement: number }> }> {
    return this.get<{ data: Array<{ keyword: string; frequency: number; avg_engagement: number }> }>(`/analytics/keywords?period=${period}`);
  }

  // Audience Analytics
  async getAudienceInsights(): Promise<{ 
    data: {
      demographics: Record<string, number>;
      interests: Array<{ interest: string; percentage: number }>;
      activity_times: Array<{ hour: number; activity: number }>;
      geographic_data: Array<{ country: string; percentage: number }>;
    }
  }> {
    return this.get<{ 
      data: {
        demographics: Record<string, number>;
        interests: Array<{ interest: string; percentage: number }>;
        activity_times: Array<{ hour: number; activity: number }>;
        geographic_data: Array<{ country: string; percentage: number }>;
      }
    }>('/analytics/audience');
  }

  // Competitive Analytics
  async getIndustryBenchmarks(industry: string): Promise<{ 
    data: {
      avg_engagement_rate: number;
      avg_posting_frequency: number;
      top_content_types: string[];
      optimal_posting_times: string[];
    }
  }> {
    return this.get<{ 
      data: {
        avg_engagement_rate: number;
        avg_posting_frequency: number;
        top_content_types: string[];
        optimal_posting_times: string[];
      }
    }>(`/analytics/industry-benchmarks?industry=${industry}`);
  }

  // ROI Analytics
  async getROIAnalytics(period = '30d'): Promise<{
    data: {
      total_reach: number;
      cost_per_engagement: number;
      conversion_rate: number;
      revenue_attributed: number;
      roi_percentage: number;
    }
  }> {
    return this.get<{
      data: {
        total_reach: number;
        cost_per_engagement: number;
        conversion_rate: number;
        revenue_attributed: number;
        roi_percentage: number;
      }
    }>(`/analytics/roi?period=${period}`);
  }

  // Real-time Analytics
  async getRealTimeMetrics(): Promise<{
    data: {
      active_posts: number;
      current_engagement: number;
      trending_hashtags: string[];
      live_mentions: number;
    }
  }> {
    return this.get<{
      data: {
        active_posts: number;
        current_engagement: number;
        trending_hashtags: string[];
        live_mentions: number;
      }
    }>('/analytics/realtime');
  }
}

export const analyticsAPI = new AnalyticsAPIService();
export default analyticsAPI; 